import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AddBeneficiaryRequest } from '../../../../core/requests/AddBeneficiaryRequest';
import { Beneficiary } from 'app/core/models/beneficiary';
import { Patch } from 'app/core/models/patch';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { CoreTranslationService } from '../../../../../@core/services/translation.service';
import { TranslatePipe } from '@ngx-translate/core';
import { BenefitSummaryService } from '../../../../core/service/benefit-summary.service';
import { AuthenticationService } from '../../../../core/service';
import { ToastrService } from 'ngx-toastr';
import { verifyIfCnpIsDiff } from '../../../../core/helpers/custom-validators';


@Component({
  selector: 'app-add-beneficiary',
  templateUrl: './add-beneficiary.component.html',
  styleUrls: ['./add-beneficiary.component.scss'],
  providers: [TranslatePipe]
})
export class AddBeneficiaryComponent implements OnInit, OnDestroy {

  @Input() public actionType: string;
  @Input() public selectedBeneficiary: Beneficiary | null;
  public addBeneficiaryForm: FormGroup | null = null;
  public checkCnpForm: FormGroup | null = null;

  private ngUnsubscribe = new Subject();
  public degreeKinship = [
    { id: 1, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_FATHER') },
    { id: 2, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_MOTHER') },
    { id: 3, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_BROTHER') },
    { id: 4, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_SISTER') },
    { id: 5, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_HUSBAND') },
    { id: 6, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_WIFE') },
    { id: 7, name: this.translate.transform('BENEFIT_SUMMARY.ADD_EDIT_FORM.KINSHIP_CHILD') },
  ];
  public tosDocumentUrl = '';
  public addBeneficiaryFormSubmitted = false;
  public userId: number;
  public beneficiaryCnp: number;
  // Visibility toggles for masked sensitive fields. Default to visible since
  // the values are freshly typed in; only masked by default when editing an
  // existing (pre-filled) beneficiary.
  public showCnp = true;
  public showPhone = true;
  public showEmail = true;
  private userCnp: string;

  constructor(
    private fb: FormBuilder,
    private benefitSummaryService: BenefitSummaryService,
    private _coreTranslationService: CoreTranslationService,
    private translate: TranslatePipe,
    private authenticationService: AuthenticationService,
    private _toastrService: ToastrService,
    public activeModal: NgbActiveModal
  ) {
    // this._coreTranslationService.translate(english, romanian);
    this.authenticationService.currentUserCompanyInfo.subscribe(x => {
      if (x) {
        this.tosDocumentUrl = x.tosDocumentUrl;
      }
    });
  }

  ngOnInit(): void {

    this.authenticationService.currentUserInfo.subscribe(x => {
      this.userCnp = x.cnp;
    });

    this.addBeneficiaryForm = new FormGroup({
      name: new FormControl('', [Validators.required]),
      cnp: new FormControl('', [Validators.required,
        Validators.pattern('^[1-9]\\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\\d|3[01])(0[1-9]|[1-4]\\d|5[0-2]|7[0-9]|8[0-2]|9[0-9])(00[1-9]|0[1-9]\\d|[1-9]\\d\\d)\\d$'), verifyIfCnpIsDiff(this.userCnp)]),
      city: new FormControl('', [Validators.required]),
      phone: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{10}$')]),
      email: new FormControl('', [Validators.required, Validators.email]),
      kinship: new FormControl(1, [Validators.required]),
      terms: new FormControl(false, [Validators.requiredTrue])

    });
    if (this.actionType === 'edit') {
      this.showCnp = false;
      this.showPhone = false;
      this.showEmail = false;
      this.addBeneficiaryForm.patchValue({
        'name': this.selectedBeneficiary.fullName,
        'cnp': this.selectedBeneficiary.cnp,
        'city': this.selectedBeneficiary.city,
        'phone': this.selectedBeneficiary.phoneNumber,
        'email': this.selectedBeneficiary.email,
        'kinship': +this.selectedBeneficiary.kinship
      });
    }
  }

  get f() {
    return this.addBeneficiaryForm.controls;
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(true);
    this.ngUnsubscribe.complete();
  }

  addBeneficiary() {
    this.addBeneficiaryFormSubmitted = true;
    if (this.addBeneficiaryForm.invalid) {
      return;
    }
    if (this.actionType === 'edit') {
      const params: any = {
        fullName: this.addBeneficiaryForm.get('name')?.value,
        cnp: this.addBeneficiaryForm.get('cnp')?.value,
        city: this.addBeneficiaryForm.get('city')?.value,
        phoneNumber: this.addBeneficiaryForm.get('phone')?.value,
        email: this.addBeneficiaryForm.get('email')?.value,
        kinship: this.addBeneficiaryForm.get('kinship')?.value
      };
      const patchData: Array<Patch> = Object.keys(this.selectedBeneficiary).filter(key => params[key] && this.selectedBeneficiary[key] !== params[key]).map(key => {
        this.selectedBeneficiary[key] = params[key];
        return {
          op: 'replace',
          path: key,
          value: params[key]
        };
      });
      this.benefitSummaryService.editBeneficiary(patchData, this.selectedBeneficiary.id)
        .subscribe(
          _ => {
            this.onEditBeneficiarySuccess();
          },
          error => {
            this.onEditBeneficiaryFail(error);
          });
    } else {
      const params: AddBeneficiaryRequest = {
        fullName: this.addBeneficiaryForm.get('name')?.value,
        cnp: this.addBeneficiaryForm.get('cnp')?.value,
        city: this.addBeneficiaryForm.get('city')?.value,
        phoneNumber: this.addBeneficiaryForm.get('phone')?.value,
        email: this.addBeneficiaryForm.get('email')?.value,
        isAgreed: true,
        isDeleted: false,
        kinship: this.addBeneficiaryForm.get('kinship')?.value,
        userId: this.authenticationService.currentUserValue.userId

      };
      this.benefitSummaryService.addBeneficiary(params)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe(
          data => {
            this.onAddBeneficiarySuccess();
          },
          error => {
            this.onAddCardFail(error);
          });
    }
  }

  public onEditBeneficiarySuccess() {
    this.activeModal.close([true, this.selectedBeneficiary]);
  }

  public onEditBeneficiaryFail(error: HttpErrorResponse) {
    this._toastrService.error(
      this.translate.transform('BENEFIT_SUMMARY.BENEFICIARY.GENERAL_FAIL'),
      this.translate.transform('ERROR'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
  }

  public onAddBeneficiarySuccess() {
    this.closeBtnClick(true);
  }

  public onAddCardFail(error: HttpErrorResponse) {
    this._toastrService.error(
      this.translate.transform('BENEFIT_SUMMARY.BENEFICIARY.GENERAL_FAIL'),
      this.translate.transform('ERROR'),
      { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
    );
  }

  public closeBtnClick(status: boolean = false) {
    this.activeModal.close([status]);
  }

}


